import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const statusBadgeVariants = cva(
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      status: {
        draft: "bg-slate-100 text-slate-800 border border-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-700",
        "under_review": "bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-700",
        "pending_approval": "bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700",
        approved: "bg-green-100 text-green-800 border border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700",
        rejected: "bg-red-100 text-red-800 border border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700",
        success: "bg-green-100 text-green-800 border border-green-200 dark:bg-green-900 dark:text-green-200 dark:border-green-700",
        warning: "bg-yellow-100 text-yellow-800 border border-yellow-200 dark:bg-yellow-900 dark:text-yellow-200 dark:border-yellow-700",
        error: "bg-red-100 text-red-800 border border-red-200 dark:bg-red-900 dark:text-red-200 dark:border-red-700",
        info: "bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-900 dark:text-blue-200 dark:border-blue-700",
      },
    },
    defaultVariants: {
      status: "draft",
    },
  },
);

export interface StatusBadgeProps 
  extends React.HTMLAttributes<HTMLDivElement>, 
    VariantProps<typeof statusBadgeVariants> {
  status: 'draft' | 'under_review' | 'pending_approval' | 'approved' | 'rejected' | 'success' | 'warning' | 'error' | 'info';
  children: React.ReactNode;
}

function StatusBadge({ className, status, children, ...props }: StatusBadgeProps) {
  return (
    <div 
      className={cn(statusBadgeVariants({ status }), className)} 
      {...props}
    >
      {children}
    </div>
  );
}

// Helper function to get status display text
export const getStatusDisplay = (status: string) => {
  switch (status) {
    case 'approved': return 'Approved';
    case 'under_review': return 'Under Review';
    case 'draft': return 'Draft';
    case 'rejected': return 'Rejected';
    case 'pending_approval': return 'Pending Approval';
    default: return status;
  }
};

// Helper function to normalize status values
export const normalizeStatus = (status: string) => {
  const statusMap: Record<string, string> = {
    'Approved': 'approved',
    'Under Review': 'under_review', 
    'Draft': 'draft',
    'Rejected': 'rejected',
    'Pending Approval': 'pending_approval',
  };
  return statusMap[status] || status.toLowerCase();
};

export { StatusBadge, statusBadgeVariants };
